import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Link2, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_LINKED = 3;

/**
 * Phase 6 — Mentorship Programme panel.
 *
 * Lives inside the Mental Hub. Allows Admin / Mental Coach / explicitly
 * permitted users to link athletes into mentorship groups (2-4 athletes per
 * group) and edit the linkage.
 *
 * Props
 * ─────
 * athletes  — array of {id, full_name, squad?}
 * focusAthleteId — when set, the panel highlights the group containing this
 *                  athlete (Phase 6 spec: "Athlete A view shows linked athletes").
 */
const MentorshipPanel = ({ athletes = [], focusAthleteId = null }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ athlete_ids: [], notes: '' });
  // Phase 81 — when picking athletes for a NEW group, anyone already in
  // another active group is shown disabled / crossed-out. When EDITING,
  // members of the group being edited remain selectable so the user can
  // keep them in.
  const [search, setSearch] = useState('');

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mentorship/links`);
      setLinks(r.data || []);
      setAccessError(false);
    } catch (e) {
      if (e.response?.status === 403) setAccessError(true);
      else toast.error('Failed to load mentorship groups');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const nameOf = (id) => athletes.find(a => a.id === id)?.full_name || id;

  const togglePicked = (aid) => {
    setForm(prev => {
      const picked = (prev.athlete_ids || []).includes(aid);
      if (picked) {
        return { ...prev, athlete_ids: prev.athlete_ids.filter(x => x !== aid) };
      }
      // Note: already-paired athletes have the `disabled` HTML attribute
      // set on their chip, so this handler never receives them. Visual
      // feedback (line-through + "paired" badge + title tooltip) is
      // sufficient — no toast needed.
      if ((prev.athlete_ids || []).length >= MAX_LINKED + 1) {
        toast.error(`A group can hold at most ${MAX_LINKED + 1} athletes`);
        return prev;
      }
      return { ...prev, athlete_ids: [...(prev.athlete_ids || []), aid] };
    });
  };

  const handleSave = async () => {
    if ((form.athlete_ids || []).length < 2) {
      toast.error('Pick at least 2 athletes'); return;
    }
    try {
      if (editTarget) {
        await axios.put(`${API}/mentorship/links/${editTarget.id}`, form);
        toast.success('Group updated');
      } else {
        await axios.post(`${API}/mentorship/links`, form);
        toast.success('Mentorship group created');
      }
      setCreateOpen(false);
      setEditTarget(null);
      setForm({ athlete_ids: [], notes: '' });
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save group');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mentorship group?')) return;
    try {
      await axios.delete(`${API}/mentorship/links/${id}`);
      toast.success('Group deleted');
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const startEdit = (link) => {
    setEditTarget(link);
    setForm({
      athlete_ids: [...(link.athlete_ids || [])],
      notes: link.notes || '',
    });
    setSearch('');
    setCreateOpen(true);
  };

  // Phase 81 — Mentorship UX. Build the set of already-paired athletes
  // so we can disable them in the picker, but keep the currently-edited
  // group's members selectable.
  const pairedAthleteIds = useMemo(() => {
    const out = new Set();
    for (const l of links) {
      if (editTarget && l.id === editTarget.id) continue;
      for (const aid of (l.athlete_ids || [])) out.add(aid);
    }
    return out;
  }, [links, editTarget]);

  const filteredAthletes = useMemo(() => {
    const term = (search || '').trim().toLowerCase();
    if (!term) return athletes;
    return athletes.filter(a => (a.full_name || '').toLowerCase().includes(term));
  }, [athletes, search]);

  if (accessError) {
    return (
      <Card data-testid="mentorship-no-access">
        <CardContent className="py-10 text-center text-sm text-gray-500">
          You don't have access to the Mentorship Programme. Ask an Admin to grant
          you <code>mental_hub.mentorship</code> view permission.
        </CardContent>
      </Card>
    );
  }

  // Highlighted group for the focused athlete (Phase 6 mentor view).
  const focusGroup = focusAthleteId
    ? links.find(l => (l.athlete_ids || []).includes(focusAthleteId))
    : null;
  const otherLinks = focusGroup ? links.filter(l => l.id !== focusGroup.id) : links;

  return (
    <div className="space-y-4" data-testid="mentorship-panel">
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-purple-600" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Mentorship Programme</h3>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Pair athletes (2–{MAX_LINKED + 1} per group) for mentor relationships and group monitoring.
          </p>
        </div>
        <Button
          onClick={() => { setEditTarget(null); setForm({ athlete_ids: [], notes: '' }); setSearch(''); setCreateOpen(true); }}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="mentorship-new-group"
        >
          <Plus className="w-4 h-4 mr-1" /> New Group
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      ) : (
        <>
          {focusGroup && (
            <Card className="border-purple-300 bg-purple-50 dark:bg-purple-900/20"
                  data-testid="mentorship-focus-group">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-purple-700" />
                  Mentorship group for {nameOf(focusAthleteId)}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-1">
                <div className="flex flex-wrap gap-1.5">
                  {(focusGroup.athlete_ids || []).map(aid => (
                    <Badge
                      key={aid}
                      className={aid === focusAthleteId
                        ? 'bg-purple-700 text-white border-purple-700'
                        : 'bg-white text-purple-800 border-purple-200'}
                      data-testid={`mentorship-focus-athlete-${aid}`}
                    >
                      {nameOf(aid)}
                    </Badge>
                  ))}
                </div>
                {focusGroup.notes && (
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-2 italic">{focusGroup.notes}</p>
                )}
                <div className="flex gap-1 mt-3">
                  <Button size="sm" variant="outline" onClick={() => startEdit(focusGroup)}
                          data-testid={`mentorship-edit-${focusGroup.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500"
                          onClick={() => handleDelete(focusGroup.id)}
                          data-testid={`mentorship-delete-${focusGroup.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {otherLinks.length === 0 && !focusGroup ? (
            <Card data-testid="mentorship-empty">
              <CardContent className="py-12 text-center text-sm text-gray-500">
                No mentorship groups yet. Create one to pair athletes for mentor monitoring.
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                 data-testid="mentorship-grid">
              {otherLinks.map(l => (
                <Card key={l.id} className="hover:shadow-md transition-shadow"
                      data-testid={`mentorship-card-${l.id}`}>
                  <CardContent className="py-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex flex-wrap gap-1.5">
                        {(l.athlete_ids || []).map(aid => (
                          <Badge key={aid} variant="outline" className="text-xs">{nameOf(aid)}</Badge>
                        ))}
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                                onClick={() => startEdit(l)}
                                data-testid={`mentorship-edit-${l.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500"
                                onClick={() => handleDelete(l.id)}
                                data-testid={`mentorship-delete-${l.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {l.notes && <p className="text-xs text-gray-500 italic">{l.notes}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) { setEditTarget(null); setForm({ athlete_ids: [], notes: '' }); } }}>
        <DialogContent className="max-w-2xl" data-testid="mentorship-dialog">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Mentorship Group' : 'New Mentorship Group'}</DialogTitle>
            <DialogDescription>
              Pick 2 to {MAX_LINKED + 1} athletes. Each athlete can belong to only one
              active group at a time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Athletes</Label>
              {/* Phase 81 — search input + disabled-state visualisation. */}
              <div className="relative mt-1 mb-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search athletes..."
                  className="pl-8 h-8 text-xs"
                  data-testid="mentorship-search"
                />
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-72 overflow-y-auto p-2 border rounded-md">
                {filteredAthletes.map(a => {
                  const picked = (form.athlete_ids || []).includes(a.id);
                  const paired = pairedAthleteIds.has(a.id);
                  const disabled = paired && !picked;
                  return (
                    <button
                      type="button"
                      key={a.id}
                      onClick={() => togglePicked(a.id)}
                      disabled={disabled}
                      title={disabled ? 'Already paired in another group' : ''}
                      className={`px-2.5 py-1 text-xs rounded-md border transition flex items-center gap-1 ${
                        picked
                          ? 'bg-purple-600 text-white border-purple-600'
                          : disabled
                          ? 'bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-600 border-gray-200 dark:border-gray-800 line-through cursor-not-allowed'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-purple-400'
                      }`}
                      data-testid={`mentorship-pick-${a.id}`}
                    >
                      <span>{a.full_name}</span>
                      {disabled && (
                        <span className="text-[9px] uppercase tracking-wide opacity-70" data-testid={`mentorship-paired-${a.id}`}>
                          paired
                        </span>
                      )}
                    </button>
                  );
                })}
                {filteredAthletes.length === 0 && (
                  <p className="text-[11px] text-gray-500 px-1 py-2" data-testid="mentorship-no-results">
                    No athletes match “{search}”.
                  </p>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                {(form.athlete_ids || []).length} of max {MAX_LINKED + 1} picked
                {pairedAthleteIds.size > 0 && (
                  <> · {pairedAthleteIds.size} athlete{pairedAthleteIds.size === 1 ? '' : 's'} already paired</>
                )}.
              </p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Mentor pairing context, goals, review cadence…"
                data-testid="mentorship-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="mentorship-save">
              {editTarget ? 'Save Changes' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MentorshipPanel;
